import * as React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Container, FadeIn, H1, Link, ScreenReaderText, Text } from "atoms";
import { ReviewableCard } from "features/reviewable";
import welcomeToTheHug from "assets/images/welcome-to-the-hug.svg";
import { useReviewableList } from "features/search";
import { useAuth } from "features/auth";
import { AuthStatus } from "../types";

const crossfadeOut = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: .25;
  }
  75%, 100% {
    opacity: 1;
  }`;

function HomeHero({ authStatus, ...props }: AuthStatus) {
  const { user } = useAuth();
  const isHugger = user?.utilities.includes("Hugger");

  const { data } = useReviewableList("home-hero-gallery");

  React.useLayoutEffect(() => {
    const carousel = document.querySelector(".HomeHeroCarousel");
    const slides = carousel?.querySelectorAll<HTMLLIElement>(".slide") ?? [];

    function nextSlide() {
      for (const slide of slides) {
        const startPosition = slide.dataset.position as string;
        let endPosition;

        endPosition = Number(startPosition) + 1;
        if (endPosition > 5) {
          endPosition = 1;
        } else if (endPosition < 1) {
          endPosition = 5;
        }
        slide.dataset.position = endPosition.toString();
      }
    }
    const interval = setInterval(nextSlide, 5500);
    return () => clearInterval(interval);
  }, []);

  return (
    <HomeHeroWrapper {...props}>
      <Container width="sm">
        <HomeHeroColumns>
          {authStatus === "user" ? (
            <HomeHeroHeadlineWelcome>
              <img src={welcomeToTheHug} alt="Welcome to The HUG" />
            </HomeHeroHeadlineWelcome>
          ) : (
            <>
              <HomeHeroHeadline>
                Discover Diverse NFT&nbsp;Creators
              </HomeHeroHeadline>

              <HomeHeroText>
                <Text>
                  HUG is an inclusive social curation community that&nbsp;helps
                  you discover and share hug-worthy NFT&nbsp;creators
                  and&nbsp;communities.
                </Text>
                {authStatus === "anon" && (
                  <Link variant="gradient" size="md" to="/sign-in">
                    Sign in to Explore
                  </Link>
                )}
                {authStatus === "user" && (
                  <Link
                    variant="gradient"
                    size="md"
                    href="https://opensea.io/collection/hugpass"
                  >
                    Buy a HUG Pass
                  </Link>
                )}
                {isHugger && (
                  <Link variant="gradient" size="md" to="/search">
                    Discover Creators
                  </Link>
                )}
              </HomeHeroText>
            </>
          )}

          <HomeHeroImage delay={1000}>
            <ScreenReaderText as="figcaption">
              Highlighted items
            </ScreenReaderText>
            <HomeHeroCarousel>
              <HomeHeroCarouselSlides className="HomeHeroCarousel">
                {data &&
                  data.data.slice(0, 5).map((item, index) => (
                    <HomeHeroCarouselSlide
                      className={`slide slide-${index}`}
                      data-position={index}
                      key={item.id}
                    >
                      <ReviewableCard entity={item} key={item.id} />
                    </HomeHeroCarouselSlide>
                  ))}
              </HomeHeroCarouselSlides>
            </HomeHeroCarousel>
          </HomeHeroImage>
        </HomeHeroColumns>
      </Container>
    </HomeHeroWrapper>
  );
}

const HomeHeroWrapper = styled.div(({ theme }) => ({
  position: "relative",
  h1: {
    textTransform: "uppercase",
  },
}));
const HomeHeroColumns = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: "'headline' 'body' 'image'",
  alignItems: "center",
  gap: "0 2rem",
  maxWidth: "100%",
  padding: "calc(2rem + 4vw) 0 3rem",
  placeItems: "center",
  position: "relative",
  zIndex: 9,
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateAreas: "'headline headline' 'body image'",
  },
  [theme.breakpoints.desktop]: {
    gridTemplateAreas: "'headline image' 'body image'",
  },
}));
const HomeHeroHeadline = styled(H1)(({ theme }) => ({
  gridArea: "headline",
  margin: 0,
  textAlign: "center",
  position: "relative",
  zIndex: 10,
  [theme.breakpoints.desktop]: {
    textAlign: "left",
    alignSelf: "end",
  },
}));
const HomeHeroHeadlineWelcome = styled.div(({ theme }) => ({
  gridArea: "headline",
  position: "relative",
  zIndex: 10,
  [theme.breakpoints.tablet]: {
    gridArea: "-1/1/1/1",
    width: "100%",
  },
  img: {
    display: "block",
    margin: "0 auto",
    width: "70%",
    maxWidth: "100%",
    [theme.breakpoints.tablet]: {
      width: "130%",
      maxWidth: "280px",
      // transform: "translateX(-10%)",
    },
    [theme.breakpoints.desktop]: {
      width: "100%",
      // transform: "translateX(0)",
    },
  },
}));
const HomeHeroText = styled.div(({ theme }) => ({
  gridArea: "body",
  textAlign: "center",
  maxWidth: "470px",
  padding: "2rem 0",
  position: "relative",
  zIndex: 10,
  [theme.breakpoints.tablet]: {
    justifySelf: "start",
    textAlign: "left",
  },
  [theme.breakpoints.desktop]: {
    alignSelf: "start",
  },
}));
const HomeHeroImage = styled(FadeIn)(({ theme }) => ({
  gridArea: "image",
  flex: "0 0 370px",
  position: "relative",
  zIndex: 9,
  [theme.breakpoints.tablet]: {
    transform: "translateX(3.2rem)",
  },
  [theme.breakpoints.desktop]: {
    transform: "translateX(0)",
  },
}));
const HomeHeroCarousel = styled.figure(({ theme }) => ({
  aspectRatio: "1 / 1.2",
  margin: "0 auto",
  position: "relative",
  width: 300,
}));
const HomeHeroCarouselSlides = styled.ul(({ theme }) => ({
  listStyleType: "none",
  margin: 0,
  padding: 0,
  width: 250,
  position: "absolute",
  top: 0,
  left: "50%",
  webkitTransformStyle: "preserve-3d",
  transform: "translateX(-50%)",
  transition: "all .5s ease",
}));
const HomeHeroCarouselSlide = styled.li(({ theme }) => ({
  backfaceVisibility: "hidden",
  position: "absolute",
  transition: "all .5s ease",
  webkitTransformStyle: "preserve-3d",
  "&[data-position='0']": {
    transform: "scale(.7) translate3d(-50%, 0px, 0px) rotate(-5deg)",
    zIndex: 1,
    filter: "blur(2px) grayscale(.6) brightness(1.2)",
    opacity: 0.75,
    pointerEvents: "none",
  },
  "&[data-position='1']": {
    transform: "scale(.85) translate3d(-20%, 0px, 0px) rotate(-3deg)",
    zIndex: 2,
    filter: "blur(1px) grayscale(.3)  brightness(1.1)",
    pointerEvents: "none",
  },
  "&[data-position='2']": {
    transform: "scale(1) translate3d(0px, 0px, 0px) rotate(0deg)",
    zIndex: 5,
  },
  "&[data-position='3']": {
    transform: "scale(.85) translate3d(20%, 0px, 0px) rotate(2deg)",
    zIndex: 4,
    filter: "blur(1px) grayscale(.3)  brightness(1.1)",
    pointerEvents: "none",
    animation: `${crossfadeOut} .5s ease-in 0s forwards`,
  },
  "&[data-position='4']": {
    transform: "scale(.7) translate3d(50%, 0px, 0) rotate(7deg)",
    zIndex: 3,
    filter: "blur(2px) grayscale(.6) brightness(1.2)",
    opacity: 0.75,
    pointerEvents: "none",
  },
  "&::before": {
    content: '"\\200B"',
  },
}));
export { HomeHero };
